import { KeyboardArrowUp } from "@mui/icons-material";
import { Alert, Box, FormHelperText, FormLabel, Link } from "@mui/joy";
import { useMemo, useState } from "react";
import { useController, useFormContext } from "react-hook-form";

import Warning from "@/assets/icons/Warning";

import FormGroup from "./FormGroup";
import { ToggleButtonSelect } from "./ToggleButtonSelect";

type Props = {
  name?: string;
  value: number | null;
  onChange: (value: number | null) => void;
  takenNumbers: number[];
  initialCount?: number;
  showWarning?: boolean;
};

export const NumberSelect = ({
  name = "number",
  value,
  onChange,
  takenNumbers,
  initialCount = 16,
  showWarning = true,
}: Props) => {
  const [maxCount, setMaxCount] = useState<number>(initialCount);

  const availableNumbers = useMemo(
    () =>
      Array.from(
        { length: Math.max(Math.max(...takenNumbers) || 1, maxCount) },
        (_, i) => i + 1,
      ).filter((x) => !takenNumbers.includes(x)),
    [takenNumbers, maxCount],
  );
  const allNumbers = useMemo(
    () => [...takenNumbers, ...availableNumbers].sort((a, b) => a - b),
    [takenNumbers, availableNumbers],
  );

  return (
    <Box>
      <ToggleButtonSelect
        // @ts-expect-error - for some reason sx has a type error
        options={allNumbers.map((x) => ({
          name: x.toString(),
          value: x.toString(),
          sx: takenNumbers.includes(x) ? { "&:not([aria-pressed=true])": { opacity: 0.5 } } : {},
        }))}
        onChange={(value) => (value ? onChange(parseInt(value)) : onChange(null))}
        value={value?.toString() ?? null}
        name={name}
        label="Number"
        sx={{
          ".MuiButton-root": {
            width: 40,
          },
        }}
      />
      {maxCount < 100 ? (
        <Link onClick={() => (maxCount >= 50 ? setMaxCount(100) : setMaxCount(50))} fontSize={14}>
          Show more
        </Link>
      ) : null}
      {showWarning && value && takenNumbers.includes(value) ? (
        <Alert startDecorator={<Warning />} sx={{ marginTop: 1.5 }} color="warning">
          This number is already taken
        </Alert>
      ) : null}
    </Box>
  );
};

export const NumberSelectInput = ({
  name,
  label,
  takenNumbers,
  initialCount = 16,
  onCollapse,
}: {
  name: string;
  label: string;
  takenNumbers: number[];
  initialCount?: number;
  /** When set, the label becomes a button that calls this to hide the field. */
  onCollapse?: () => void;
}) => {
  const { control } = useFormContext();
  const { field, fieldState } = useController({
    name,
    control,
  });
  return (
    <FormGroup error={Boolean(fieldState.error)}>
      <FormLabel onClick={onCollapse} sx={onCollapse ? { cursor: "pointer" } : undefined}>
        {label}
        {onCollapse ? <KeyboardArrowUp /> : null}
      </FormLabel>
      <NumberSelect
        value={field.value}
        onChange={field.onChange}
        takenNumbers={takenNumbers}
        initialCount={initialCount}
        showWarning={!fieldState.error}
      />
      <FormHelperText>{fieldState.error?.message}</FormHelperText>
    </FormGroup>
  );
};
